import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import ShareIcon from '@mui/icons-material/Share'
import { Box, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import Cookies from 'js-cookie'
import { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'
import IconText from '@/components/IconText'
import { useAppSelector } from '@/hooks/redux'
import { useDuplicateCase } from '@/hooks/useDuplicateCase'
import { useDeleteCaseMutation } from '@/store/apiService'
import { clearSelectedCases } from '@/store/project'
import { setCurrentCaseId } from '@/store/sessionSlice'

import ShareProject from './ShareProject'

interface ProjectsToolbarProps {
  tabValue: number
  selectedRows: string[]
  refetch: () => void
  setSearch: (value: string) => void
  search: string
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void
}

const ProjectsToolbar = ({
  tabValue,
  handleTabChange,
  selectedRows,
  refetch,
  setSearch,
  search,
}: ProjectsToolbarProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'project',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'project.projectActions',
  })

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [deleteCase] = useDeleteCaseMutation()

  const [isShareDialog, setIsShareDialog] = useState(false)
  const [input, setInput] = useState(search)
  const [isDeleteDialog, setIsDeleteDialog] = useState(false)
  const location = useLocation()
  const useQuery = () => {
    return new URLSearchParams(location.search)
  }
  const query = useQuery()
  const urlCaseId = query.get('caseId') || ''

  const caseIds = useAppSelector((state) => state.project.selectedCaseIds)

  const name = useAppSelector((state) => state.project.selectedCaseName)
  const { duplicate } = useDuplicateCase()

  const initialData = {
    id: caseIds?.[0],
    patient_name: name?.[0],
  }

  const deleteCases = async () => {
    if (caseIds) {
      try {
        if (urlCaseId && caseIds.includes(urlCaseId)) {
          dispatch(setCurrentCaseId(''))
          // If we are deleting the active case, navigate away or clear param
          const newSearchParams = new URLSearchParams(location.search)
          newSearchParams.delete('caseId')
          navigate({
            pathname: location.pathname,
            search: newSearchParams.toString(),
          })
        }

        await deleteCase({
          ids: [caseIds as any],
          hard_delete: false,
        }).unwrap()
        setIsDeleteDialog(false)
        toast.success('Case deleted successfully')
        dispatch(clearSelectedCases())
        refetch()
      } catch (err) {
        toast.error('Failed to delete case')
      }
    }
  }

  const duplicateCases = async () => {
    if (caseIds) {
      await duplicate({ ids: [caseIds as any], refetch })
    }
  }

  const shareCase = () => {
    setIsShareDialog(true)
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value)
      }, 500),
    [setSearch],
  )

  useEffect(() => {
    setInput(search)
  }, [search])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInput(val)
    debouncedSearch(val)
  }

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  return (
    <>
      <Stack>
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(128, 128, 128, 0.55)' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label={t('myProjects')} sx={tabStyle} />
            <Tab label={t('sharedProjects')} sx={tabStyle} />
          </Tabs>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            tabValue === 0 && selectedRows?.length > 0
              ? 'space-between'
              : 'flex-end'
          }
          spacing={2}
          py={3}
        >
          {tabValue === 0 && selectedRows?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#464646',
                borderRadius: '4px',
                px: 2,
                py: 1,
                flexGrow: 1,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  sx={{ color: 'white', fontSize: '14px', fontWeight: 400 }}
                >
                  {selectedRows?.length} {t('selected')}:
                </Typography>
                {selectedRows?.length <= 1 && (
                  <>
                    <IconText
                      icon={<OpenInNewIcon />}
                      label={translate('openProject').toUpperCase()}
                      onClick={() => {
                        navigate(
                          `/projects/${selectedRows[0]}?caseId=${selectedRows[0]}`,
                          {
                            state: { project: selectedRows[0] },
                          },
                        )
                        dispatch(clearSelectedCases())
                      }}
                    />
                    <IconText
                      icon={<ShareIcon />}
                      label={translate('share').toUpperCase()}
                      onClick={shareCase}
                    />
                  </>
                )}
                <IconText
                  icon={<ContentCopyIcon />}
                  label={translate('duplicate').toUpperCase()}
                  onClick={duplicateCases}
                />
                <IconText
                  icon={<DeleteIcon />}
                  label={translate('delete').toUpperCase()}
                  onClick={() => {
                    setIsDeleteDialog(true)
                  }}
                />
              </Stack>

              <Icon
                icon={LuX}
                size={18}
                onClick={() => dispatch(clearSelectedCases())}
              />
            </Box>
          )}

          <TextField
            size="small"
            placeholder={t('search')}
            variant="outlined"
            InputProps={{
              endAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
            }}
            sx={searchFieldStyle}
            onChange={handleSearchChange}
            value={input}
          />

          {tabValue === 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DeleteIcon sx={{ fontSize: '22px !important' }} />}
              label={t('trash')}
              sx={{ fontSize: '15px', fontWeight: 500 }}
              onClick={() => {
                if (urlCaseId) {
                  navigate(`/projects/trash?caseId=${urlCaseId}`)
                } else {
                  navigate('/projects/trash')
                }
              }}
              textTransform="uppercase"
            />
          )}
        </Stack>
      </Stack>
      {isShareDialog && (
        <ShareProject
          open={isShareDialog}
          onClose={() => setIsShareDialog(false)}
          initialData={initialData}
          refetchRecipe={refetch}
        />
      )}
      <Dialog
        triggerElement={null}
        isOpen={isDeleteDialog}
        onClose={() => {
          setIsDeleteDialog(false)
        }}
        title={t('deleteProjectDialog.title')}
        content={
          <Typography>{t('deleteProjectDialog.description')}</Typography>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={t('deleteProjectDialog.cancelButton')}
              onClick={() => {
                setIsDeleteDialog(false)
              }}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={t('deleteProjectDialog.deleteButton')}
              onClick={deleteCases}
              textTransform="uppercase"
            />
          </Box>
        }
      />
    </>
  )
}

const tabStyle = {
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 700,
  color: '#999999',
  '&.Mui-selected': {
    color: 'primary.main',
  },
}

const searchFieldStyle = {
  input: { color: 'white' },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#454545',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#90CAF9',
    },
    '& input': {
      '&:-webkit-autofill': {
        boxShadow: '0 0 0 1000px #292929 inset',
        transition: 'background-color 5000s ease-in-out 0s',
      },
    },
  },
}

export default ProjectsToolbar
